import { ExportFormatOption, ResolvedExportScale, formatExportDate, formatScaleLabel } from "./exportFormats";

export interface ExportTitleBlockData {
  projectName: string;
  formatLabel: string;
  scaleLabel?: string;
  updatedAtLabel: string;
}

export interface ExportAnnotationOptions {
  titleBlock: ExportTitleBlockData;
  format: Pick<ExportFormatOption, "supportsScale" | "paperWidthMm" | "paperHeightMm">;
}

interface TitleBlockFrameOptions {
  minWidthPx?: number;
  minHeightPx?: number;
}

/** Minimum preview overlay size (download / add-to-presentation) so stacked labels + values fit, including date descenders. */
export const PREVIEW_TITLE_BLOCK_MIN_SIZE_PX = { width: 88, height: 78 } as const;

export interface ExportTitleBlockFrame {
  width: number;
  height: number;
  marginRight: number;
  marginBottom: number;
}

const TITLE_BLOCK_MM = {
  width: 36,
  height: 28,
  marginRight: 6,
  marginBottom: 6,
};

const TITLE_BLOCK_LAYOUT = {
  /** Left column width in the middle row (Scale | Format). */
  scaleWidthRatio: 0.34,
  topRowHeightRatio: 0.43,
  middleRowHeightRatio: 0.3,
  bottomRowHeightRatio: 0.27,
};

const TITLE_BLOCK_TYPOGRAPHY = {
  labelFontRatio: 0.072,
  /** Single size for all value lines (project, scale, format, date). */
  valueFontRatio: 0.102,
};

const TITLE_BLOCK_SPACING = {
  horizontalPaddingRatio: 0.03,
};

/** Row height fractions for the title block (sum = 1). Used by preview and canvas layout. */
export const exportTitleBlockRowLayout = {
  top: TITLE_BLOCK_LAYOUT.topRowHeightRatio,
  middle: TITLE_BLOCK_LAYOUT.middleRowHeightRatio,
  bottom: TITLE_BLOCK_LAYOUT.bottomRowHeightRatio,
} as const;

export const exportTitleBlockGridTemplateRows = `${exportTitleBlockRowLayout.top}fr ${exportTitleBlockRowLayout.middle}fr ${exportTitleBlockRowLayout.bottom}fr`;

/** Middle row column split (Scale | Format), matches `scaleWidthRatio`. */
export const exportTitleBlockMiddleGridTemplateColumns = `${TITLE_BLOCK_LAYOUT.scaleWidthRatio}fr ${1 - TITLE_BLOCK_LAYOUT.scaleWidthRatio}fr`;

const drawStackedField = (
  context: CanvasRenderingContext2D,
  label: string,
  value: string,
  x: number,
  y: number,
  width: number,
  height: number,
  paddingX: number,
  labelFontSize: number,
  valueFontSize: number,
) => {
  const maxTextWidth = Math.max(0, width - paddingX * 2);
  const paddingTop = height * 0.12;
  const labelY = y + paddingTop;
  const valueY = labelY + labelFontSize * 1.2 + height * 0.025;

  context.fillStyle = "#555555";
  context.font = `${labelFontSize}px sans-serif`;
  context.textAlign = "left";
  context.textBaseline = "top";
  context.fillText(label.toUpperCase(), x + paddingX, labelY, maxTextWidth);

  context.fillStyle = "#111111";
  context.font = `600 ${valueFontSize}px sans-serif`;
  context.fillText(value, x + paddingX, valueY, maxTextWidth);
};

const getPixelsPerMm = (
  canvasWidth: number,
  canvasHeight: number,
  format?: Pick<ExportFormatOption, "supportsScale" | "paperWidthMm" | "paperHeightMm">,
) => {
  if (!format?.supportsScale || !format.paperWidthMm || !format.paperHeightMm) {
    return {
      x: canvasWidth / 297,
      y: canvasHeight / 210,
    };
  }

  return {
    x: canvasWidth / format.paperWidthMm,
    y: canvasHeight / format.paperHeightMm,
  };
};

export const getExportTitleBlockFrame = (
  canvasWidth: number,
  canvasHeight: number,
  format?: Pick<ExportFormatOption, "supportsScale" | "paperWidthMm" | "paperHeightMm">,
  options?: TitleBlockFrameOptions,
): ExportTitleBlockFrame => {
  const pixelsPerMm = getPixelsPerMm(canvasWidth, canvasHeight, format);
  const baseWidth = TITLE_BLOCK_MM.width * pixelsPerMm.x;
  const baseHeight = TITLE_BLOCK_MM.height * pixelsPerMm.y;
  const widthScale = options?.minWidthPx ? options.minWidthPx / baseWidth : 1;
  const heightScale = options?.minHeightPx ? options.minHeightPx / baseHeight : 1;
  const scale = Math.max(1, widthScale, heightScale);

  return {
    width: baseWidth * scale,
    height: baseHeight * scale,
    marginRight: TITLE_BLOCK_MM.marginRight * pixelsPerMm.x,
    marginBottom: TITLE_BLOCK_MM.marginBottom * pixelsPerMm.y,
  };
};

export const buildExportTitleBlockData = (
  format: Pick<ExportFormatOption, "label">,
  resolvedScale: Pick<ResolvedExportScale, "scaleDenominator">,
  projectName: string,
  updatedAt?: string | null,
  includeScale = true,
): ExportTitleBlockData => ({
  projectName: projectName?.trim() || "Untitled",
  formatLabel: format.label,
  ...(includeScale ? { scaleLabel: formatScaleLabel(resolvedScale) } : {}),
  updatedAtLabel: formatExportDate(updatedAt),
});

export const buildExportAnnotationOptions = (
  format: Pick<ExportFormatOption, "label" | "supportsScale" | "paperWidthMm" | "paperHeightMm">,
  resolvedScale: Pick<ResolvedExportScale, "scaleDenominator">,
  projectName: string,
  updatedAt?: string | null,
  includeTitleBlock = true,
  includeScale = true,
): ExportAnnotationOptions | undefined => {
  if (!includeTitleBlock) {
    return undefined;
  }

  return {
    titleBlock: buildExportTitleBlockData(format, resolvedScale, projectName, updatedAt, includeScale),
    format,
  };
};

export const drawExportTitleBlock = (
  context: CanvasRenderingContext2D,
  canvasWidth: number,
  canvasHeight: number,
  titleBlock: ExportTitleBlockData,
  format?: Pick<ExportFormatOption, "supportsScale" | "paperWidthMm" | "paperHeightMm">,
  frameOverride?: ExportTitleBlockFrame,
) => {
  const frame = frameOverride ?? getExportTitleBlockFrame(canvasWidth, canvasHeight, format);
  const boxLeft = canvasWidth - frame.width - frame.marginRight;
  const boxTop = canvasHeight - frame.height - frame.marginBottom;
  const topRowHeight = frame.height * TITLE_BLOCK_LAYOUT.topRowHeightRatio;
  const middleRowHeight = frame.height * TITLE_BLOCK_LAYOUT.middleRowHeightRatio;
  const bottomRowHeight = frame.height * TITLE_BLOCK_LAYOUT.bottomRowHeightRatio;
  const topRowBottom = boxTop + topRowHeight;
  const middleRowBottom = topRowBottom + middleRowHeight;
  const showScale = titleBlock.scaleLabel !== undefined;
  const scaleCellRight = boxLeft + frame.width * TITLE_BLOCK_LAYOUT.scaleWidthRatio;
  const formatCellLeft = showScale ? scaleCellRight : boxLeft;
  const cellPaddingX = frame.width * TITLE_BLOCK_SPACING.horizontalPaddingRatio;
  const labelFontSize = Math.max(8, frame.height * TITLE_BLOCK_TYPOGRAPHY.labelFontRatio);
  const valueFontSize = Math.max(10, frame.height * TITLE_BLOCK_TYPOGRAPHY.valueFontRatio);

  context.fillStyle = "rgba(255, 255, 255, 0.96)";
  context.fillRect(boxLeft, boxTop, frame.width, frame.height);

  context.strokeStyle = "#444444";
  context.lineWidth = Math.max(1, frame.height * 0.012);
  context.beginPath();
  context.rect(boxLeft, boxTop, frame.width, frame.height);
  context.moveTo(boxLeft, topRowBottom);
  context.lineTo(boxLeft + frame.width, topRowBottom);
  context.moveTo(boxLeft, middleRowBottom);
  context.lineTo(boxLeft + frame.width, middleRowBottom);
  if (showScale) {
    context.moveTo(scaleCellRight, topRowBottom);
    context.lineTo(scaleCellRight, middleRowBottom);
  }
  context.stroke();

  drawStackedField(
    context,
    "Project",
    titleBlock.projectName,
    boxLeft,
    boxTop,
    frame.width,
    topRowHeight,
    cellPaddingX,
    labelFontSize,
    valueFontSize,
  );
  if (showScale) {
    drawStackedField(
      context,
      "Scale",
      titleBlock.scaleLabel!,
      boxLeft,
      topRowBottom,
      scaleCellRight - boxLeft,
      middleRowHeight,
      cellPaddingX,
      labelFontSize,
      valueFontSize,
    );
  }
  drawStackedField(
    context,
    "Format",
    titleBlock.formatLabel,
    formatCellLeft,
    topRowBottom,
    boxLeft + frame.width - formatCellLeft,
    middleRowHeight,
    cellPaddingX,
    labelFontSize,
    valueFontSize,
  );
  drawStackedField(
    context,
    "Date",
    titleBlock.updatedAtLabel,
    boxLeft,
    middleRowBottom,
    frame.width,
    bottomRowHeight,
    cellPaddingX,
    labelFontSize,
    valueFontSize,
  );
};

/**
 * Render the title block to a small offscreen canvas using the same Canvas 2D
 * drawing code as the final export, then return a data-URL image. This ensures
 * the preview overlay is pixel-identical to the exported result.
 */
export const renderTitleBlockPreviewImage = (
  previewWidth: number,
  previewHeight: number,
  titleBlock: ExportTitleBlockData,
  format: Pick<ExportFormatOption, "supportsScale" | "paperWidthMm" | "paperHeightMm">,
  frame: ExportTitleBlockFrame,
): string | null => {
  const dpr = window.devicePixelRatio || 1;
  const canvas = document.createElement("canvas");
  canvas.width = Math.ceil(frame.width * dpr);
  canvas.height = Math.ceil(frame.height * dpr);

  const ctx = canvas.getContext("2d");
  if (!ctx) return null;

  ctx.scale(dpr, dpr);

  // Translate so drawExportTitleBlock's bottom-right positioning lands at (0,0)
  const boxLeft = previewWidth - frame.width - frame.marginRight;
  const boxTop = previewHeight - frame.height - frame.marginBottom;
  ctx.translate(-boxLeft, -boxTop);

  // Only render labels (PROJECT, SCALE, etc.) without values so text doesn't overlap at small preview sizes.
  const labelsOnly: ExportTitleBlockData = {
    projectName: "",
    formatLabel: "",
    ...(titleBlock.scaleLabel === undefined ? {} : { scaleLabel: "" }),
    updatedAtLabel: "",
  };
  drawExportTitleBlock(ctx, previewWidth, previewHeight, labelsOnly, format, frame);

  return canvas.toDataURL("image/png");
};

export const composeExportWithTitleBlock = (
  drawingCanvas: HTMLCanvasElement,
  outputWidth: number,
  outputHeight: number,
  annotationOptions: ExportAnnotationOptions,
): HTMLCanvasElement => {
  const outputCanvas = document.createElement("canvas");
  outputCanvas.width = outputWidth;
  outputCanvas.height = outputHeight;

  const context = outputCanvas.getContext("2d");
  if (!context) {
    return drawingCanvas;
  }

  context.fillStyle = "#FFFFFF";
  context.fillRect(0, 0, outputWidth, outputHeight);
  context.drawImage(drawingCanvas, 0, 0, drawingCanvas.width, drawingCanvas.height, 0, 0, outputWidth, outputHeight);
  drawExportTitleBlock(context, outputWidth, outputHeight, annotationOptions.titleBlock, annotationOptions.format);

  return outputCanvas;
};
